// views/GameView.jsx: M2 hot-seat demo + real-game shell. M3-8 split
// this out of App.jsx; the view is now mounted only when the URL is
// loaded with ?dev_game=1 (M3-8 makes the room view the default
// landing surface). M2 functionality is otherwise unchanged.
//
// Original M2.c-4 trick-area + animation demo shell.
//
// Demo states:
//   • bid_r1, bid_r2, bid_r2_std, bidding controls (M2.c-3)
//   • trick_play, trump=♥, 4 cards on the table, ready to resolve
//   • trick_resolved, post-animation, pile of 1 next to winner (seat 3)
//
// The "Resolve trick (600ms)" button triggers a real rAF-driven animation
//, sweep + winner flash + pulse, then advances to trick_resolved when
// the timer completes. Reduced-motion is honored via matchMedia.
//
// Real game-loop driving arrives in M2.c-7+.

import { useEffect, useLayoutEffect, useRef, useState, useCallback } from 'preact/hooks';
import { readDevicePixelRatio, watchDevicePixelRatio, prepareHiDpiCanvas, backingSize, keepStageAligned } from '../canvas/hidpi.js';
import { computeTableLayout } from '../canvas/layout.js';
import { renderTable, describeAriaLabel } from '../canvas/table.js';
import { renderScoreboard } from '../canvas/scoreboard.js';
import { renderAllHands } from '../canvas/hand.js';
import { renderAnnotations } from '../canvas/annotations.js';
import { renderUpCard } from '../canvas/upcard.js';
import { renderTrickArea, describeTrickWinner, _internals as trickInternals } from '../canvas/trick-area.js';
import { isBower, _internals as cardInternals } from '../canvas/cards.js';
import { BiddingControls } from '../ui/BiddingControls.jsx';
import { EndOfHandModal } from '../ui/EndOfHandModal.jsx';
import { SettingsPanel, SettingsGearButton } from '../ui/SettingsPanel.jsx';
import { useSettings } from '../ui/useSettings.js';
import { useTrickAnimation, usePrefersReducedMotion } from '../ui/useTrickAnimation.js';
import { useBotAnnotations } from '../ui/useBotAnnotations.js';
import { RealGame } from '../ui/RealGame.jsx';
import * as audio from '../audio/events.js';
import { DealSmoke } from '../DealSmoke.jsx';

const { SUIT_NAME } = cardInternals;
const { RESOLUTION_DURATION_MS, REDUCED_DURATION_MS } = trickInternals;

// Test hook: ?firstSeed=42 pins the real-game first-game seed. Used
// by Playwright specs that need deterministic deals (e.g., discard
// spec needs to land dealer_seat=1 to exercise human-dealer flow).
function readSeedFromQuery() {
  if (typeof window === 'undefined') return undefined;
  const v = new URLSearchParams(window.location.search).get('firstSeed');
  if (!v) return undefined;
  const n = parseInt(v, 10);
  return Number.isFinite(n) ? n : undefined;
}

// Test hook: ?forceDealer=1..4 pins the first-hand dealer. Without
// this the dealer always starts as seat 1 (state.lua default), so
// the M2.c-11 discard spec needs it set to 1 explicitly anyway.
function readDealerFromQuery() {
  if (typeof window === 'undefined') return undefined;
  const v = new URLSearchParams(window.location.search).get('forceDealer');
  if (!v) return undefined;
  const n = parseInt(v, 10);
  if (n === 1 || n === 2 || n === 3 || n === 4) {
    return /** @type {1|2|3|4} */ (n);
  }
  return undefined;
}

const DEMO_HANDS = {
  1: ['JH', 'JD', 'AH', 'KS', '9C'],
  2: ['AS', 'TS', 'QS', 'KH', '9H'],
  3: ['JC', 'QH', 'KC', 'TH', 'AC'],
  4: ['QD', 'KC', '9S', '9D', 'AD'],
};

const SEAT_LABELS = { 1: 'Player 1', 2: 'Bot Atom', 3: 'Bot Caprica', 4: 'Bot HAL' };

/** @type {Record<string, object>} */
const DEMO_STATES = {
  bid_r1: {
    current_state: 'bid_r1', current_turn_seat: 1, dealer_seat: 4,
    stick_the_dealer: true, trump_suit: null, maker_seat: null,
    up_card: '9H', up_card_turned_down: false, bid_history: [],
    hands: DEMO_HANDS,
    scores: { team_a: 0, team_b: 0 },
    hand_index: 1, game_target: 10,
  },
  bid_r2: {
    current_state: 'bid_r2', current_turn_seat: 1, dealer_seat: 4,
    stick_the_dealer: true, trump_suit: null, maker_seat: null,
    up_card: '9H', up_card_turned_down: true,
    bid_history: [
      { round: 1, action: 'pass', seat: 1 },
      { round: 1, action: 'pass', seat: 2 },
      { round: 1, action: 'pass', seat: 3 },
      { round: 1, action: 'pass', seat: 4 },
    ],
    hands: DEMO_HANDS,
    scores: { team_a: 2, team_b: 1 },
    hand_index: 3, game_target: 10,
  },
  bid_r2_std: {
    current_state: 'bid_r2', current_turn_seat: 1, dealer_seat: 1,
    stick_the_dealer: true, trump_suit: null, maker_seat: null,
    up_card: '9H', up_card_turned_down: true,
    bid_history: [
      { round: 1, action: 'pass', seat: 2 },
      { round: 1, action: 'pass', seat: 3 },
      { round: 1, action: 'pass', seat: 4 },
      { round: 1, action: 'pass', seat: 1 },
      { round: 2, action: 'pass', seat: 2 },
      { round: 2, action: 'pass', seat: 3 },
      { round: 2, action: 'pass', seat: 4 },
    ],
    hands: DEMO_HANDS,
    scores: { team_a: 4, team_b: 3 },
    hand_index: 6, game_target: 10,
  },
  // Trick play, 4 cards already on the table, trump=Hearts, maker=seat 3.
  // Clubs led (9C from seat 1); seats 2, 3 and 4 follow with clubs. JC is
  // a plain club here: the left bower for Hearts is JD (the jack of the
  // other red suit), not JC, so no trump is on the table. The demo still
  // scripts seat 3 as the winner (TRICK_WINNER_SEAT) to drive the sweep
  // animation; by the rules AC (seat 4) would take this trick. (Maker is
  // seat 3 so the flash color is Hearts red.)
  trick_play: {
    current_state: 'trick_play', current_turn_seat: 1, dealer_seat: 4,
    stick_the_dealer: true, trump_suit: 'H', maker_seat: 3,
    up_card: null, up_card_turned_down: true,
    bid_history: [], hands: DEMO_HANDS,
    trick_plays: [
      { seat: 1, card_id: '9C' },
      { seat: 2, card_id: 'KC' },   // KC, clubs (followed suit)
      { seat: 3, card_id: 'JC' },   // JC, clubs (not a bower: JD is the left bower for Hearts)
      { seat: 4, card_id: 'AC' },   // AC, clubs (followed suit)
    ],
    // Clubs led, only 9C is legal for seat 1 (must follow suit). The
    // other four hand cards gray out via canvas/hand.js's legality
    // overlay.
    legal_plays: { 1: ['9C'] },
    trick_counts_per_seat: {},
    scores: { team_a: 6, team_b: 4 },
    hand_index: 9, game_target: 10,
  },
  trick_resolved: {
    current_state: 'trick_play', current_turn_seat: 1, dealer_seat: 4,
    stick_the_dealer: true, trump_suit: 'H', maker_seat: 3,
    up_card: null, up_card_turned_down: true,
    bid_history: [], hands: DEMO_HANDS,
    trick_plays: [],
    trick_counts_per_seat: { 3: 1 },  // seat 3 won
    scores: { team_a: 6, team_b: 4 },
    hand_index: 9, game_target: 10,
  },
  // End-of-hand state: team A "made the march" (5 tricks). Player (seat
  // 1) is the maker, trump=Hearts. Demo exercises plan AC #1 (modal
  // shows after a completed hand) + AC #3 march branch. The post-hand
  // reveal flag is on by default for the first 10 hands per DM2-9.
  end_of_hand: {
    current_state: 'end_of_hand', current_turn_seat: null, dealer_seat: 4,
    stick_the_dealer: true, trump_suit: 'H', maker_seat: 1,
    up_card: null, up_card_turned_down: true,
    bid_history: [], hands: DEMO_HANDS,
    trick_plays: [],
    trick_counts_per_seat: { 1: 2, 3: 3 },
    scores: { team_a: 9, team_b: 8 },
    hand_index: 13, game_target: 10,
    post_hand_reveal: true,
    // The 5 trick winners in chronological order (used for the recap row).
    tricks: [
      { winner_seat: 1, winning_card: 'JH' },
      { winner_seat: 3, winning_card: 'JD' },
      { winner_seat: 1, winning_card: 'AH' },
      { winner_seat: 3, winning_card: 'TH' },
      { winner_seat: 3, winning_card: 'KC' },
    ],
    // Pillar 4 score breakdown, matches what state.lua's score_hand
    // will populate into _public.score_summary once M2.c-10 wires the
    // real game loop.
    score_summary: {
      result: 'march',
      scoring_team: 'team_a',
      delta: 2,
      prev_scores: { team_a: 7, team_b: 8 },
      new_scores:  { team_a: 9, team_b: 8 },
      was_loner: false,
      maker_seat: 1,
      tricks_per_seat: { 1: 2, 3: 3 },
    },
  },
};

const DEMO_CYCLE = ['bid_r1', 'bid_r2', 'bid_r2_std', 'trick_play', 'trick_resolved', 'end_of_hand'];

const TRICK_WINNER_SEAT = 3;

const RANK_SPOKEN = { J: 'Jack', Q: 'Queen', K: 'King', A: 'Ace', T: 'Ten' };

function spokenRank(cardId) {
  return RANK_SPOKEN[cardId[0]] ?? cardId[0];
}

function describeHandAnnouncement(hand, trumpSuit) {
  if (!hand || hand.length === 0) return 'Your hand is empty.';
  return 'Your hand: ' + hand.map((cardId) => {
    const rank = spokenRank(cardId);
    const suit = SUIT_NAME[cardId[1]];
    const bower = isBower(cardId, trumpSuit);
    const suffix = bower === 'right' ? ' (right bower)'
                 : bower === 'left'  ? ' (left bower)'
                 : '';
    return `${rank} of ${suit}${suffix}`;
  }).join(', ') + '.';
}

export function GameView() {
  /** @type {{ current: HTMLCanvasElement | null }} */
  const canvasRef = useRef(null);
  /** @type {{ current: HTMLDivElement | null }} */
  const stageRef = useRef(null);
  const [layout, setLayout] = useState(() =>
    computeTableLayout(window.innerWidth, window.innerHeight));
  // devicePixelRatio, re-read on zoom or a move to another screen.
  const [dpr, setDpr] = useState(() => readDevicePixelRatio());
  useEffect(() => watchDevicePixelRatio(setDpr), []);
  const [demoKey, setDemoKey] = useState('bid_r1');
  const [appMode, setAppMode] = useState(/** @type {'demo'|'real'} */ ('demo'));
  useLayoutEffect(() => {
    const stage = stageRef.current;
    if (!stage) return undefined;
    return keepStageAligned(stage, dpr);
  }, [appMode, dpr]);
  const [hoveredCardId, setHoveredCardId] = useState(/** @type {string|null} */ (null));
  const [focusedCardId, setFocusedCardId] = useState(/** @type {string|null} */ (null));
  /** @type {[Array<{card_id:string,x:number,y:number,w:number,h:number,legal:boolean,bower:'right'|'left'|null}>, (h:any)=>void]} */
  const [playerHits, setPlayerHits] = useState(/** @type {any[]} */ ([]));
  const [lastBid, setLastBid] = useState(/** @type {string|null} */ (null));
  const [resolvingTrick, setResolvingTrick] = useState(false);
  const [trickWinnerAnnouncement, setTrickWinnerAnnouncement] = useState('');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settings, updateSettings] = useSettings();

  // Effective reduced motion is OS preference OR user override per
  // M2.c-8 plan. Override is one-way (force ON); a user can't force OFF
  // motion that the OS / accessibility stack has requested.
  const osReducedMotion = usePrefersReducedMotion();
  const reducedMotion = osReducedMotion || settings.reduced_motion_override;
  const resolutionDuration = reducedMotion ? REDUCED_DURATION_MS : RESOLUTION_DURATION_MS;
  const { annotations, pushAnnotation, clearAll: clearAnnotations } = useBotAnnotations(reducedMotion);

  const state = DEMO_STATES[demoKey];

  // rAF time accumulator for the trick-resolution animation.
  const elapsedMs = useTrickAnimation({
    active: resolvingTrick,
    durationMs: resolutionDuration,
    onComplete: () => {
      audio.emit(audio.EVENTS.TRICK_WON_YOURS, { winner_seat: TRICK_WINNER_SEAT });
      setTrickWinnerAnnouncement(
        describeTrickWinner(TRICK_WINNER_SEAT, SEAT_LABELS));
      setResolvingTrick(false);
      setDemoKey('trick_resolved');
    },
  });

  useEffect(() => {
    let timer;
    function onResize() {
      clearTimeout(timer);
      timer = setTimeout(() => {
        setLayout(computeTableLayout(window.innerWidth, window.innerHeight));
      }, 150);
    }
    window.addEventListener('resize', onResize);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  // Canvas render loop, runs on every elapsed-ms tick during animation.
  useEffect(() => {
    if (!canvasRef.current) return;
    // Backing store = CSS size x devicePixelRatio, context scaled by the
    // ratio: every renderer below still draws in CSS px (hidpi.js).
    const ctx = prepareHiDpiCanvas(canvasRef.current, layout.canvasW, layout.canvasH, dpr);
    if (!ctx) return;
    renderTable(ctx, state, layout);
    renderScoreboard(ctx, state, layout);
    if (state.current_state === 'bid_r1') {
      renderUpCard(ctx, state, layout);
    } else if (state.current_state === 'bid_r2') {
      renderUpCard(ctx, state, layout);
    }
    const hits = renderAllHands(ctx, layout, state, { hoveredCardId, focusedCardId });
    setPlayerHits(hits);
    // Trick area only when current_state is trick_play (or its post-anim sibling).
    let trickCards = [];
    if (state.trick_plays !== undefined) {
      trickCards = renderTrickArea(ctx, state, layout, {
        trick_resolution: resolvingTrick ? {
          winner_seat: TRICK_WINNER_SEAT,
          elapsed_ms: elapsedMs,
          reduced_motion: reducedMotion,
        } : null,
      });
    }
    // Pillar 5 bot annotations, render last so they layer over plates
    // + cards. Gated by settings.heuristic_disclosure outside the hook
    // by funnelling pushes through handleAnnotationDemo.
    renderAnnotations(ctx, layout, annotations);
    // Drawn-card records (CSS px) for browser tests: the hand hit zones
    // and the trick cards this frame.
    canvasRef.current.dataset.drawnCards = JSON.stringify({ hand: hits, trick: trickCards });
  }, [layout, dpr, demoKey, hoveredCardId, focusedCardId, resolvingTrick, elapsedMs, reducedMotion, annotations]);

  const handleBid = useCallback((bid) => {
    // Demo logs the would-be bid; the M2.c-3 bidding spec asserts a
    // confirm-alone click triggers this log. RealGame.jsx routes real
    // bids through dispatch instead.
    console.log('[bid] would submit:', bid);
    const label = bid.suit ? `${bid.action} ${bid.suit}` : bid.action;
    setLastBid(label);
  }, []);

  // Demo card click, logs the would-be play for legal cards. M2.c-2
  // hand-display ACs depend on this (illegal click does NOT log; legal
  // click does). Real game submission lives in RealGame.jsx.
  const handleDemoCardClick = useCallback((hit) => {
    if (!hit.legal) return;
    console.log('[hand] play (would submit):', hit.card_id);
  }, []);

  const handleResolveTrick = useCallback(() => {
    setDemoKey('trick_play');
    setTrickWinnerAnnouncement('');
    // Wait a frame so the state swap renders, then trigger animation.
    requestAnimationFrame(() => setResolvingTrick(true));
  }, []);

  const announcement = describeHandAnnouncement(state.hands[1], state.trump_suit);
  const isBidding = state.current_state === 'bid_r1' || state.current_state === 'bid_r2';
  const isTrickPhase = demoKey === 'trick_play' || demoKey === 'trick_resolved';
  const isHandOver  = state.current_state === 'end_of_hand';

  const handleContinue = useCallback(() => {
    // M2.c-7 dismisses the modal by cycling back to the bidding demo.
    // M2.c-10 (game loop) will swap this for state.advance_to_deal().
    setDemoKey('bid_r1');
  }, []);

  // Pillar 5 demo: simulate two bots emitting reasoning strings. Real
  // bot annotations land via state._private.bot_annotation once the
  // M2.c-10 game loop wires them up; until then the demo trigger
  // exercises the rendering + lifecycle path.
  const handleAnnotationDemo = useCallback(() => {
    if (!settings.heuristic_disclosure) return;
    pushAnnotation(2, 'Bot Atom: Strong trump hand, ordering up.');
    pushAnnotation(4, 'Bot HAL: Two bowers and an off-ace.');
  }, [settings.heuristic_disclosure, pushAnnotation]);

  return (
    <>
      <div style={{ marginBottom: 8, display: 'flex', gap: 4 }}>
        <button
          type="button"
          data-app-mode="demo"
          onClick={() => setAppMode('demo')}
          aria-pressed={appMode === 'demo'}
          style={{
            background: appMode === 'demo' ? '#5A7A9A' : 'transparent',
            color: appMode === 'demo' ? '#1A1422' : '#F5F5F0',
            border: '1px solid #5A7A9A',
            borderRadius: 4,
            padding: '4px 12px',
            fontFamily: 'inherit',
            fontSize: 12,
            cursor: 'pointer',
          }}
        >
          Demos
        </button>
        <button
          type="button"
          data-app-mode="real"
          onClick={() => setAppMode('real')}
          aria-pressed={appMode === 'real'}
          style={{
            background: appMode === 'real' ? '#3DBA52' : 'transparent',
            color: appMode === 'real' ? '#F5F5F0' : '#F5F5F0',
            border: '1px solid #3DBA52',
            borderRadius: 4,
            padding: '4px 12px',
            fontFamily: 'inherit',
            fontSize: 12,
            cursor: 'pointer',
          }}
        >
          Real game
        </button>
      </div>
      <SettingsGearButton
        onClick={() => setSettingsOpen(true)}
        style={{ marginBottom: 8 }}
      />
      <SettingsPanel
        open={settingsOpen}
        settings={settings}
        onUpdate={updateSettings}
        onClose={() => setSettingsOpen(false)}
        showStickTheDealer
      />
      {appMode === 'real' ? (
        <RealGame
          settings={{
            stick_the_dealer:        settings.stick_the_dealer,
            heuristic_disclosure:    settings.heuristic_disclosure,
            reduced_motion_override: settings.reduced_motion_override,
          }}
          firstSeed={readSeedFromQuery()}
          forceDealerSeat={readDealerFromQuery()}
        />
      ) : (<>
      <div
        ref={stageRef}
        style={{
          position: 'relative',
          width: layout.canvasW,
          height: layout.canvasH,
        }}
      >
        <canvas
          ref={canvasRef}
          id="game-canvas"
          width={backingSize(layout.canvasW, layout.canvasH, dpr).width}
          height={backingSize(layout.canvasW, layout.canvasH, dpr).height}
          role="img"
          aria-label={describeAriaLabel(state)}
          style={{ display: 'block', width: `${layout.canvasW}px`, height: `${layout.canvasH}px` }}
        />
        {playerHits.map((hit) => {
          const suit = hit.card_id[1];
          const rank = spokenRank(hit.card_id);
          const label = hit.bower
            ? `${rank} of ${SUIT_NAME[suit]} (${hit.bower} bower)`
            : `${rank} of ${SUIT_NAME[suit]}`;
          return (
            <button
              key={hit.card_id}
              type="button"
              data-card-id={hit.card_id}
              aria-label={label}
              aria-disabled={!hit.legal}
              onMouseEnter={() => setHoveredCardId(hit.card_id)}
              onMouseLeave={() => setHoveredCardId(null)}
              onFocus={() => setFocusedCardId(hit.card_id)}
              onBlur={() => setFocusedCardId(null)}
              onClick={() => handleDemoCardClick(hit)}
              style={{
                position: 'absolute',
                left: hit.x, top: hit.y, width: hit.w, height: hit.h,
                background: 'transparent', border: 'none', padding: 0,
                cursor: hit.legal ? 'pointer' : 'default', outline: 'none',
              }}
            />
          );
        })}
        {isBidding && (
          <BiddingControls
            state={state}
            seat={1}
            layout={layout}
            onBid={handleBid}
            seatLabels={{ 1: 'You', 2: 'Bot Atom', 3: 'Bot Caprica', 4: 'Bot HAL' }}
          />
        )}
      </div>
      <EndOfHandModal
        open={isHandOver}
        summary={state.score_summary}
        tricks={state.tricks}
        partnerHand={state.hands?.[3]}
        trumpSuit={state.trump_suit}
        showPartnerReveal={!!state.post_hand_reveal}
        seatLabels={{ 1: 'You', 2: 'Bot Atom', 3: 'Bot Caprica', 4: 'Bot HAL' }}
        onContinue={handleContinue}
      />
      {/* aria-live regions */}
      <div
        aria-live="polite"
        aria-atomic="true"
        id="hand-announcement"
        style={{
          position: 'absolute',
          width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden',
          clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0,
        }}
      >
        {announcement}
      </div>
      <div
        aria-live="polite"
        aria-atomic="true"
        id="trick-announcement"
        data-trick-winner={trickWinnerAnnouncement}
        style={{
          position: 'absolute',
          width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden',
          clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0,
        }}
      >
        {trickWinnerAnnouncement}
      </div>
      <div class="status ready">
        {demoKey} {isBidding && '— Pillar 1 CTAs'} {isTrickPhase && '— Pillar 3 trick anim'}
        {lastBid && <span style={{ marginLeft: 12, color: '#F0C040' }}>
          — last bid: <code data-test-last-bid>{lastBid}</code>
        </span>}
        {reducedMotion && <span style={{ marginLeft: 12, color: '#9A8AB8' }}>
          (reduced motion)
        </span>}
      </div>
      <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {DEMO_CYCLE.map((k) => (
          <button
            key={k}
            type="button"
            data-demo-key={k}
            onClick={() => {
              setDemoKey(k);
              setResolvingTrick(false);
              setTrickWinnerAnnouncement('');
            }}
            style={{
              background: k === demoKey ? '#5A7A9A' : 'transparent',
              color: k === demoKey ? '#1A1422' : '#F5F5F0',
              border: '1px solid #5A7A9A',
              borderRadius: 4,
              padding: '4px 10px',
              fontFamily: 'inherit',
              fontSize: 12,
              cursor: 'pointer',
            }}
          >
            {k}
          </button>
        ))}
        {(demoKey === 'trick_play' || demoKey === 'trick_resolved') && (
          <button
            type="button"
            data-action="resolve-trick"
            onClick={handleResolveTrick}
            disabled={resolvingTrick}
            style={{
              background: '#3DBA52',
              color: '#F5F5F0',
              border: 'none',
              borderRadius: 4,
              padding: '4px 12px',
              fontFamily: 'inherit',
              fontSize: 12,
              fontWeight: 600,
              cursor: resolvingTrick ? 'not-allowed' : 'pointer',
              opacity: resolvingTrick ? 0.6 : 1,
            }}
          >
            {resolvingTrick ? 'resolving…' : `Resolve trick (${resolutionDuration}ms)`}
          </button>
        )}
        <button
          type="button"
          data-action="trigger-annotations"
          onClick={handleAnnotationDemo}
          disabled={!settings.heuristic_disclosure}
          aria-disabled={!settings.heuristic_disclosure}
          title={settings.heuristic_disclosure
            ? 'Push two bot annotations for ~3s'
            : 'Disabled — turn on "Show bot reasoning" in settings'}
          style={{
            background: settings.heuristic_disclosure ? '#5A7A9A' : 'transparent',
            color: settings.heuristic_disclosure ? '#1A1422' : '#5A5A5A',
            border: '1px solid #5A7A9A',
            borderRadius: 4,
            padding: '4px 12px',
            fontFamily: 'inherit',
            fontSize: 12,
            fontWeight: 600,
            cursor: settings.heuristic_disclosure ? 'pointer' : 'not-allowed',
            opacity: settings.heuristic_disclosure ? 1 : 0.5,
          }}
        >
          Trigger bot annotations
        </button>
      </div>
      <DealSmoke />
      </>)}
    </>
  );
}
